/**
 * @file clause-text.ts
 * @description 查核條文文字的排版解析（純函式，無 React 依賴，供 ChecklistField / 表單標籤 / 單測共用）。
 *
 * 2026-09-25 客戶回報序53–58（套用到系統全部表單）：
 *  1. 必查核標記統一：條文中的 `★` / `★★` / `(★★)` / `（★★）` 一律正規化為全形「（★★）」，
 *     並於畫面上以**紅色**顯示（與備註「註：（★★）見證評鑑缺失之增修條文為必查核重點」一致）。
 *  2. 帶（★★）標記的條文內容以**黑色粗體**顯示：
 *     - clause.mustAudit=true → 整條條文粗體，首段前自動補紅色（★★）；
 *     - 條文 / 說明中段落自帶（★★）→ 該段粗體；其後以括號或「N、」開頭的續行（審查內容、子項）一併粗體。
 *  3. 分段換行：
 *     - 文字中的換行（`\n`）一律視為段落分隔（舊渲染把換行折疊成空白，造成多段擠在一行）；
 *     - 同一行內出現多個（★★）時，每個（★★）各自起新段（序55）；
 *     - 同一行內出現連續編號的 `(1)` `(2)` …（半形或全形括號，至少兩項）時，各項各自起新段（序56）；
 *       編號須位於行首、空白或句讀分隔符（：:。；;，,）之後，句中數量 / 引用（如「每公頃(1)公噸」）不拆。
 */
/** 系統統一使用的必查核標記文字（全形括號 + 兩顆星） */
export const MUST_AUDIT_MARK = '（★★）';
/**
 * 識別各種寫法的星號標記：`（★★）`、`(★★)`、`( ★ )`、`★★`、`★`。
 * 括號內允許前後空白；括號可為全形或半形。
 */
const STAR_MARK_RE = /[（(]\s*★{1,2}\s*[）)]|★{1,2}/g;
/** 行內編號 `(1)` / `（1）`：只認阿拉伯數字（避免誤拆「(一)」「(三)流通」等法規引用） */
const ENUM_RE = /[（(](\d{1,2})[）)]/g;
/**
 * 行內編號前一字元的允許集合：只有位於行首、空白、或句讀分隔符（：:。；;，,）之後的 `(N)`
 * 才視為清單項起點；否則如「每公頃(1)公噸以下、(2)公噸以上」「第(1)款及第(2)款」屬句中數量 /
 * 引用，不拆段。
 */
const ENUM_LEAD_RE = /[\s：:。；;，,]/;
/**
 * 段落開頭若僅為條文編號（如 `12.2`、`3.`、`1.1.2`），其後緊接的（★★）不視為新段落起點，
 * 而是該段自己的標記（例如附註「3.（★★）為見證評鑑…」）。
 */
const CODE_PREFIX_RE = /^\d+(?:\.\d+)*\.?$/;
/**
 * （★★）前一字元若為冒號 / 頓號 / 左括號引號，代表標記是句中引用（如「註：（★★）見證評鑑…」
 * 「△為…、（★★）為…」），不拆段。
 */
const INLINE_LEAD_RE = /[：:、「『（(]$/;
/** （★★）段落的續行：以左括號或「N、」開頭（說明 / 子項），沿用前段粗體 */
const CONTINUATION_RE = /^(?:[（(]|\d+、)/;
/**
 * 把各種星號寫法正規化為「（★★）」，並去掉標記後多餘的空白。
 *
 * @example normalizeStarMarks('(★★) 生產地') === '（★★）生產地'
 */
export function normalizeStarMarks(text) {
    return text
        .replace(STAR_MARK_RE, MUST_AUDIT_MARK)
        .replace(/（★★）[ \t\u3000]+/g, MUST_AUDIT_MARK);
}
/**
 * 在一行文字中找出「連續編號」`(1)(2)(3)…` 的起始位置（至少兩項才生效）。
 * 只收錄從 1 開始、逐一遞增的編號，避免把「(2) 年」這類單獨引用誤判成清單。
 */
function findEnumerationBreaks(line) {
    const positions = [];
    let expected = 1;
    for (const m of line.matchAll(ENUM_RE)) {
        const idx = m.index ?? 0;
        // 前一字元須為行首 / 空白 / 句讀分隔符，否則屬句中引用（不推進 expected）
        if (idx > 0 && !ENUM_LEAD_RE.test(line[idx - 1]))
            continue;
        if (Number(m[1]) === expected) {
            positions.push(idx);
            expected += 1;
        }
    }
    return positions.length >= 2 ? positions : [];
}
/**
 * 找出一行中每個（★★）的位置，作為新段落起點。
 * 位於行首、前面只有條文編號（如「3.」）、或屬句中引用（前一字元為冒號 / 頓號等）者不拆。
 */
function findMarkBreaks(line) {
    const positions = [];
    let idx = line.indexOf(MUST_AUDIT_MARK);
    while (idx !== -1) {
        const before = line.slice(0, idx).trim();
        if (idx > 0 && before !== '' && !CODE_PREFIX_RE.test(before) && !INLINE_LEAD_RE.test(before)) {
            positions.push(idx);
        }
        idx = line.indexOf(MUST_AUDIT_MARK, idx + MUST_AUDIT_MARK.length);
    }
    return positions;
}
/**
 * 把條文文字拆成段落字串（已正規化星號標記）。
 *
 * 規則（依序）：換行 → 行內多個（★★）→ 行內連續編號 (1)(2)…；空段落捨棄。
 */
export function splitClauseParagraphs(text) {
    const normalized = normalizeStarMarks(text ?? '');
    const out = [];
    for (const rawLine of normalized.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!line)
            continue;
        const breaks = Array.from(new Set([...findMarkBreaks(line), ...findEnumerationBreaks(line)].filter((p) => p > 0))).sort((a, b) => a - b);
        let start = 0;
        for (const pos of breaks) {
            const piece = line.slice(start, pos).trim();
            if (piece)
                out.push(piece);
            start = pos;
        }
        const tail = line.slice(start).trim();
        if (tail)
            out.push(tail);
    }
    return out;
}
/** 把一個段落字串切成「普通文字 / 標記」片段 */
function toSegments(paragraph) {
    const segments = [];
    const parts = paragraph.split(MUST_AUDIT_MARK);
    parts.forEach((part, i) => {
        if (i > 0)
            segments.push({ text: MUST_AUDIT_MARK, mark: true });
        if (part)
            segments.push({ text: part, mark: false });
    });
    return segments;
}
/**
 * 解析條文 / 說明文字為可渲染的段落結構。
 *
 * @param text 原始文字（可含換行、各式星號寫法、行內 (1)(2) 編號）
 * @param opts.mustAudit 條文是否為必查核重點（schema 的 clause.mustAudit）：
 *   true 時首段前補（★★）（若首段未自帶）且全部段落粗體。
 * @returns 段落陣列；空文字回傳空陣列
 */
export function parseClauseText(text, opts = {}) {
    const paragraphs = splitClauseParagraphs(text);
    if (opts.mustAudit) {
        if (paragraphs.length === 0)
            paragraphs.push(MUST_AUDIT_MARK);
        else if (!paragraphs[0].startsWith(MUST_AUDIT_MARK)) {
            paragraphs[0] = MUST_AUDIT_MARK + paragraphs[0];
        }
    }
    // 續行判定：緊接在（★★）段落之後、以括號或「N、」開頭的段落（如「（審查內容：1、…」「2、…」
    // 「(1) …」）屬於同一條（★★）條文的延續，一併粗體；遇到其他段落即中止延續。
    let inMarked = false;
    return paragraphs.map((p) => {
        const marked = p.includes(MUST_AUDIT_MARK);
        const continuation = !marked && inMarked && CONTINUATION_RE.test(p);
        inMarked = marked || continuation;
        return { segments: toSegments(p), bold: !!opts.mustAudit || marked || continuation };
    });
}
/** 文字中是否含任何星號標記（供標籤等輕量場景判斷是否需要富文字渲染） */
export function hasStarMark(text) {
    STAR_MARK_RE.lastIndex = 0;
    const found = STAR_MARK_RE.test(text);
    STAR_MARK_RE.lastIndex = 0;
    return found;
}
//# sourceMappingURL=clause-text.js.map